'use client';
import React, { useEffect, useRef } from 'react';

const rows = [
  { metric: 'Time-on-paint', driveThru: '90 sec automated', professional: '2–5 hours hand work', highlight: true },
  { metric: 'Chemical pH', driveThru: '11–13 alkaline strip', professional: '7.0 pH-neutral formula', highlight: false },
  { metric: 'Brush contact', driveThru: 'Recycled brushes / cloth', professional: 'Zero contact wash + microfiber', highlight: true },
  { metric: 'Swirl risk', driveThru: 'High (cross-contamination)', professional: 'Eliminated (two-bucket method)', highlight: false },
  { metric: 'Coating longevity', driveThru: '2–4 weeks wax', professional: 'Up to 6 years ceramic', highlight: false },
  { metric: 'Comes to you', driveThru: '✗', professional: '✓ Office, garage, or driveway', highlight: true },
];

export default function ComparisonSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section id="compare" ref={sectionRef} className="py-24 md:py-32" style={{ background: 'var(--carbon-mid)' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="metric-pill mb-5 reveal">Side-by-Side Comparison</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '-0.03em' }}
          >
            Drive-through wash<br />
            <span style={{ color: 'var(--aluminum-dim)' }}>vs.</span>{' '}
            <span style={{ color: 'var(--ceramic-blue)' }}>Professional detail.</span>
          </h2>
        </div>

        <div className="reveal reveal-delay-2 rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(63,63,70,0.7)' }}>
          {/* Header row */}
          <div
            className="grid grid-cols-3 text-center"
            style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(63,63,70,0.7)' }}
          >
            <div className="py-5 px-4" style={{ borderRight: '1px solid rgba(63,63,70,0.7)' }}>
              <span style={{ fontSize: '12px', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aluminum-dim)' }}>
                Metric
              </span>
            </div>
            <div className="py-5 px-4 flex flex-col items-center gap-1" style={{ borderRight: '1px solid rgba(63,63,70,0.7)' }}>
              <span style={{ fontSize: '12px', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aluminum-dim)' }}>
                Drive-Through Wash
              </span>
            </div>
            <div className="py-5 px-4 flex flex-col items-center gap-1">
              <span style={{ fontSize: '12px', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ceramic-blue)' }}>
                Form Finish Auto Care
              </span>
              <span
                className="inline-block"
                style={{ fontSize: '10px', background: 'rgba(59,130,246,0.15)', color: 'var(--ceramic-blue)', borderRadius: '9999px', padding: '2px 8px', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}
              >
                Professional
              </span>
            </div>
          </div>

          {/* Data rows */}
          {rows?.map((row, i) => (
            <div
              key={row?.metric}
              className="compare-row grid grid-cols-3"
            >
              <div
                className="py-4 px-5 flex items-center"
                style={{
                  borderRight: '1px solid rgba(63,63,70,0.7)',
                  fontSize: '14px',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 600,
                  color: 'var(--polished-aluminum)',
                }}
              >
                {row?.metric}
              </div>
              <div
                className="py-4 px-5 flex items-center justify-center text-center"
                style={{
                  borderRight: '1px solid rgba(63,63,70,0.7)',
                  fontSize: '13px',
                  color: 'var(--aluminum-dim)',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {row?.driveThru}
              </div>
              <div
                className="py-4 px-5 flex items-center justify-center text-center"
                style={{
                  fontSize: '13px',
                  color: row?.highlight ? 'var(--ceramic-blue)' : 'rgba(212,212,216,0.9)',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: row?.highlight ? 500 : 400,
                }}
              >
                {row?.professional}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 reveal reveal-delay-3">
          <a href="#book" className="btn-primary">
            Book Your Detail
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}